import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../app/providers/AuthProvider";
import use123zapStore from "../../../stores/123zapStore";
import {
  getCompleteSignupProfile,
  saveCompleteSignupImage,
  saveCompleteSignupProfile,
} from "../api/completeSignup";
import { authRoutes } from "../config/authRoutes";
import { fetchAddressByCep } from "../utils/completeSignupAddress";
import {
  getFirstCompleteSignupError,
  validateCompleteSignupForm,
} from "../utils/completeSignupValidation";

const defaultFormData = {
  person_type: "PJ",
  firstName: "",
  lastName: "",
  cpf_cnpj: "",
  telephone: "",
  cellphone: "",
  email: "",
  email_cc: "",
  zipcode: "",
  street: "",
  number: "",
  neighborhood: "",
  city: "",
  state: "",
  plan_id: 10,
};

function onlyNumbers(value) {
  return value ? String(value).replace(/\D/g, "") : "";
}

function mergeLoadedFormData(current, loadedData, dirtyFields) {
  const next = { ...current };

  Object.entries(loadedData).forEach(([fieldName, value]) => {
    if (value == null || value === "") return;
    if (dirtyFields.has(fieldName)) return;

    next[fieldName] = value;
  });

  return next;
}

function getImageErrorMessage(response) {
  const message =
    typeof response === "string" ? response : response?.message || response?.error;

  if (String(message).toLowerCase().includes("invalid image format")) {
    return "Formato de imagem inválido.";
  }

  return "Não foi possível atualizar a foto.";
}

function buildCompleteSignupPayload(formData) {
  const accountName = String(formData.firstName || "").trim();
  const responsibleName = String(formData.lastName || "").trim();

  return {
    ...formData,
    trade_name: accountName,
    company_name: accountName,
    legal_name: accountName,
    responsible_name: responsibleName,
    contact_name: responsibleName,
  };
}

export function useCompleteSignupForm() {
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const setFinishedRegister = use123zapStore((state) => state.setFinishedRegister);
  const loadPlanoAtual = use123zapStore((state) => state.loadPlanoAtual);
  const dirtyFieldsRef = useRef(new Set());
  const didBootstrapRef = useRef(false);
  const userPicture = user?.picture;

  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});
  const [formMessage, setFormMessage] = useState("");
  const [imageHelperText, setImageHelperText] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(userPicture || "");

  const isPJ = formData.person_type === "PJ";

  const accountInitial = useMemo(() => {
    const source = formData.firstName || formData.lastName || "Z";
    return source.trim().charAt(0).toUpperCase() || "Z";
  }, [formData.firstName, formData.lastName]);

  useEffect(() => {
    setProfileImage((current) => current || userPicture || "");
  }, [userPicture]);

  useEffect(() => {
    let active = true;

    async function loadProfile() {
      try {
        if (!didBootstrapRef.current) {
          didBootstrapRef.current = true;

          const refreshedUser = await refreshUser(true);
          const refreshedProfileComplete =
            String(refreshedUser?.completeProfile).toLowerCase() === "true";

          if (!active) return;

          if (refreshedProfileComplete) {
            navigate(authRoutes.dashboard, { replace: true });
            return;
          }

          setProfileImage((current) => current || refreshedUser?.picture || "");
          setInitialLoading(false);
        }

        const response = await getCompleteSignupProfile();

        if (!active) return;

        if (response?.status === "success" || response?.email || response?.name) {
          const loadedFormData = {
            cpf_cnpj: response.cpf_cnpj,
            telephone: onlyNumbers(response.address?.telephone),
            cellphone: onlyNumbers(response.address?.cellphone),
            zipcode: response.address?.postal_code,
            street: response.address?.street,
            number: response.address?.number,
            neighborhood: response.address?.neighborhood,
            city: response.address?.city,
            state: response.address?.state,
          };

          setFormData((current) =>
            mergeLoadedFormData(current, loadedFormData, dirtyFieldsRef.current)
          );

          setProfileImage(
            response.picture ?? response.avatar ?? response.profile_image ?? userPicture ?? "",
          );
        }
      } catch (error) {
        if (active) {
          setFormMessage("Não foi possível carregar seus dados. Preencha o formulário para continuar.");
        }
      } finally {
        if (active) setInitialLoading(false);
      }
    }

    loadProfile();

    return () => {
      active = false;
    };
  }, [navigate, refreshUser, userPicture]);

  function updateField(name, value) {
    dirtyFieldsRef.current.add(name);
    setFormData((current) => ({ ...current, [name]: value }));
    setFormMessage("");

    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: undefined }));
    }
  }

  function handleChange(event) {
    const { name, value } = event.target;
    const nextValue = name === "state" ? value.toUpperCase().slice(0, 2) : value;

    updateField(name, nextValue);
  }

  async function handleCepBlur() {
    const address = await fetchAddressByCep(formData.zipcode);

    if (!address) return;

    setFormData((current) => ({ ...current, ...address }));
    setErrors((current) => {
      const nextErrors = { ...current };

      Object.entries(address).forEach(([fieldName, value]) => {
        if (value) delete nextErrors[fieldName];
      });

      return nextErrors;
    });
  }

  async function handleProfileImageChange(event) {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setImageHelperText("Selecione uma imagem válida.");
      return;
    }

    const reader = new FileReader();

    reader.onload = async () => {
      const base64Image = reader.result;

      try {
        setImageLoading(true);
        setImageHelperText("");

        const response = await saveCompleteSignupImage(base64Image);

        if (response?.status === "success") {
          setProfileImage(base64Image);
          await refreshUser(true);
          return;
        }

        setImageHelperText(getImageErrorMessage(response));
      } catch (error) {
        setImageHelperText("Não foi possível atualizar a foto.");
      } finally {
        setImageLoading(false);
      }
    };

    reader.readAsDataURL(file);
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const validationErrors = validateCompleteSignupForm(formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setFormMessage(validationErrors[getFirstCompleteSignupError(validationErrors)]);
      return;
    }

    try {
      setLoading(true);
      setFormMessage("");

      const response = await saveCompleteSignupProfile(
        buildCompleteSignupPayload(formData)
      );

      if (response?.status !== "success") {
        setFormMessage("Não foi possível finalizar seu cadastro. Verifique os dados e tente novamente.");
        return;
      }

      setFinishedRegister(true);
      await refreshUser(true);
      await loadPlanoAtual(true);
      navigate(authRoutes.dashboard, { replace: true });
    } catch (error) {
      setFormMessage("Não foi possível finalizar seu cadastro. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }

  return {
    accountInitial,
    errors,
    formData,
    formMessage,
    handleCepBlur,
    handleChange,
    handleProfileImageChange,
    handleSubmit,
    imageHelperText,
    imageLoading,
    initialLoading,
    isPJ,
    loading,
    profileImage,
  };
}
